// features/projects/project-excavate.js
// ═══════════════════════════════════════════════════════════
// PROJECT EXCAVATE — full-screen 50/50 split (Progress | Notes)
//
// Replaces the Solo Layer 2 (Lens + Depth) drawer for Project mode.
// Mounts as a full-bleed overlay covering the workstation interior
// when the [Excavate] button is clicked. Reuses the same close
// button position as the existing Layer 2.
//
// Layout:
//   ┌──────────────────────────────────────────────────────────┐
//   │  Progress              ┃              Notes      [Close] │
//   │  ─────────             ┃              ─────              │
//   │  • item 1              ┃   [+ Note]                      │
//   │  • item 2              ┃   ── filter chips ──            │
//   │  ☑ item 3              ┃   • finding: ...                │
//   │  [+ Add item]          ┃   • question: ... (→ item 1)    │
//   └──────────────────────────────────────────────────────────┘
//
// Divider is draggable. Default 50/50. Drag state lives in this
// component only — not persisted.
//
// Tags (notes): finding · question · decision · blocker ·
//               breakthrough · hypothesis  (always optional)
//
// One-way link: a Note can carry progress_item_id; we render a
// chip showing the linked Progress item title. Progress items
// show "N notes" count + click to filter the Notes panel.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

const TAG_OPTIONS = [
  { id: 'finding',       label: 'Finding',       color: '#c8a96e' },
  { id: 'question',      label: 'Question',      color: '#9b8fc4' },
  { id: 'decision',      label: 'Decision',      color: '#c8a96e' },
  { id: 'blocker',       label: 'Blocker',       color: '#c46050' },
  { id: 'breakthrough',  label: 'Breakthrough',  color: '#7a9e82' },
  { id: 'hypothesis',    label: 'Hypothesis',    color: '#4db8b8' },
];
const TAG_BY_ID = Object.fromEntries(TAG_OPTIONS.map(t => [t.id, t]));

export default function ProjectExcavate({ workspaceId, userId, visible, onClose }) {
  // ── Data ──
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Split ratio (left fraction 0–1) ──
  const [leftFraction, setLeftFraction] = useState(0.5);
  const containerRef = useRef(null);
  const dragRef = useRef(null);

  // ── Filter state for Notes ──
  // null = show all, 'unlinked' = only notes without progress_item_id,
  // tag id = only notes with that tag, item id = only notes linked to that item
  const [noteFilter, setNoteFilter] = useState(null);

  // ── Load both lists when the panel opens ──
  const reloadItems = useCallback(async () => {
    if (!workspaceId) return;
    const r = await fetch(`/api/project-excavate?resource=items&workspace_id=${workspaceId}`);
    const j = await r.json();
    if (Array.isArray(j.items)) setItems(j.items);
  }, [workspaceId]);

  const reloadNotes = useCallback(async () => {
    if (!workspaceId) return;
    const r = await fetch(`/api/project-excavate?resource=notes&workspace_id=${workspaceId}`);
    const j = await r.json();
    if (Array.isArray(j.notes)) setNotes(j.notes);
  }, [workspaceId]);

  useEffect(() => {
    if (!visible || !workspaceId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([reloadItems(), reloadNotes()]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [visible, workspaceId, reloadItems, reloadNotes]);

  // ── Divider drag ──
  function startDrag(e) {
    e.preventDefault();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { rectLeft: rect.left, rectWidth: rect.width };
    function onMove(ev) {
      const drag = dragRef.current;
      if (!drag) return;
      const x = ev.clientX - drag.rectLeft;
      const frac = Math.max(0.2, Math.min(0.8, x / drag.rectWidth));
      setLeftFraction(frac);
    }
    function onUp() {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  // ── Notes by item id (for the count badge) ──
  const notesByItem = useMemo(() => {
    const map = {};
    for (const n of notes) {
      if (!n.progress_item_id) continue;
      map[n.progress_item_id] = (map[n.progress_item_id] || 0) + 1;
    }
    return map;
  }, [notes]);

  // ── Filtered notes ──
  const filteredNotes = useMemo(() => {
    if (!noteFilter) return notes;
    if (noteFilter === 'unlinked') return notes.filter(n => !n.progress_item_id);
    if (TAG_BY_ID[noteFilter]) return notes.filter(n => n.tag === noteFilter);
    // assume it's an item id
    return notes.filter(n => n.progress_item_id === noteFilter);
  }, [notes, noteFilter]);

  if (!visible) return null;

  const filterLabel = noteFilter
    ? (TAG_BY_ID[noteFilter]?.label
        || (noteFilter === 'unlinked' ? 'Unlinked' : (items.find(i => i.id === noteFilter)?.title || 'Filtered')))
    : null;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: '#07070a',
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Header bar ─────────────────────────────────────
          Header uses the SAME flex weights as the body below
          (leftFraction / 4px spacer / 1-leftFraction) so the
          column boundary lines up exactly with the body's drag
          handle as the user resizes. */}
      <div
        style={{
          height: 38,
          borderBottom: '1px solid #1e1e2a',
          background: '#07070a',
          display: 'flex',
          alignItems: 'stretch',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            flex: leftFraction,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            padding: '0 18px',
          }}
        >
          <span
            style={{
              fontSize: 10,
              color: '#c8a96e',
              fontFamily: 'Georgia, serif',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
          >
            Progress
          </span>
          <span style={{ marginLeft: 10, fontSize: 11, color: '#666' }}>
            {items.filter(i => i.status === 'done').length}/{items.length}
          </span>
        </div>
        {/* 4px non-interactive spacer matching the body's drag handle */}
        <div style={{ width: 4, background: '#1e1e2a', flexShrink: 0 }} />
        <div
          style={{
            flex: 1 - leftFraction,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            padding: '0 18px',
          }}
        >
          <span
            style={{
              fontSize: 10,
              color: '#9b8fc4',
              fontFamily: 'Georgia, serif',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
          >
            Notes
          </span>
          <span style={{ marginLeft: 10, fontSize: 11, color: '#666' }}>
            {notes.length}
          </span>
          <div style={{ flex: 1 }} />
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #2a2a2a',
              borderRadius: 4,
              color: '#666',
              padding: '4px 10px',
              fontSize: 11,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#c46050'; e.currentTarget.style.borderColor = '#c46050'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#666'; e.currentTarget.style.borderColor = '#2a2a2a'; }}
          >
            Close
          </button>
        </div>
      </div>

      {/* ── Body: Progress | divider | Notes ───────────── */}
      <div
        ref={containerRef}
        style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}
      >
        <div style={{ flex: leftFraction, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <ProgressPanel
            items={items}
            notesByItem={notesByItem}
            workspaceId={workspaceId}
            userId={userId}
            onChanged={reloadItems}
            onFilterByItem={(id) => setNoteFilter(id)}
            loading={loading}
          />
        </div>

        {/* Drag handle */}
        <div
          onMouseDown={startDrag}
          style={{
            width: 4,
            cursor: 'col-resize',
            background: '#1e1e2a',
            flexShrink: 0,
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#363650'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#1e1e2a'; }}
        />

        <div style={{ flex: 1 - leftFraction, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <NotesPanel
            notes={filteredNotes}
            allItems={items}
            filter={noteFilter}
            filterLabel={filterLabel}
            onClearFilter={() => setNoteFilter(null)}
            onSetFilter={setNoteFilter}
            workspaceId={workspaceId}
            userId={userId}
            onChanged={reloadNotes}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PROGRESS PANEL
// ═══════════════════════════════════════════════════════════
function ProgressPanel({ items, notesByItem, workspaceId, userId, onChanged, onFilterByItem, loading }) {
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');

  async function addItem() {
    const title = newTitle.trim();
    if (!title) return;
    await fetch('/api/project-excavate?resource=items', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ workspace_id: workspaceId, user_id: userId, title }),
    });
    setNewTitle('');
    setAdding(false);
    onChanged();
  }

  async function toggleStatus(item) {
    const next = item.status === 'done' ? 'open' : 'done';
    await fetch('/api/project-excavate?resource=items', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: item.id, status: next }),
    });
    onChanged();
  }

  async function saveEdit(item) {
    const title = editingTitle.trim();
    if (!title) { setEditingId(null); return; }
    if (title === item.title) { setEditingId(null); return; }
    await fetch('/api/project-excavate?resource=items', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: item.id, title }),
    });
    setEditingId(null);
    onChanged();
  }

  async function deleteItem(item) {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    await fetch(`/api/project-excavate?resource=items&id=${item.id}`, { method: 'DELETE' });
    onChanged();
  }

  // ── Accept a proposed item — flips proposed=false ─────
  async function acceptItem(item) {
    await fetch('/api/project-excavate?resource=items', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: item.id, proposed: false }),
    });
    onChanged();
  }

  // ── Dismiss a proposed item — DELETE outright ─────────
  async function dismissItem(item) {
    await fetch(`/api/project-excavate?resource=items&id=${item.id}`, { method: 'DELETE' });
    onChanged();
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
      {loading && items.length === 0 && (
        <div style={{ color: '#666', fontSize: 12, fontStyle: 'italic' }}>Loading…</div>
      )}

      {!loading && items.length === 0 && !adding && (
        <div style={{ color: '#666', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>
          <div style={{ marginBottom: 8 }}>No progress items yet.</div>
          <div style={{ fontSize: 11, opacity: 0.6 }}>
            Add steps for this project. Shift will start proposing items as you chat.
          </div>
        </div>
      )}

      {items.map(item => item.proposed ? (
        <ProposedItemRow
          key={item.id}
          item={item}
          onAccept={() => acceptItem(item)}
          onDismiss={() => dismissItem(item)}
        />
      ) : (
        <div
          key={item.id}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '8px 10px',
            borderRadius: 6,
            marginBottom: 2,
            cursor: 'default',
            background: 'transparent',
            transition: 'background 0.1s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <button
            onClick={() => toggleStatus(item)}
            title={item.status === 'done' ? 'Mark open' : 'Mark complete'}
            style={{
              flexShrink: 0,
              marginTop: 2,
              width: 16,
              height: 16,
              borderRadius: 3,
              border: `1.5px solid ${item.status === 'done' ? '#7a9e82' : '#444'}`,
              background: item.status === 'done' ? '#7a9e82' : 'transparent',
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              color: '#07070a',
            }}
          >
            {item.status === 'done' && '✓'}
          </button>

          <div style={{ flex: 1, minWidth: 0 }}>
            {editingId === item.id ? (
              <input
                autoFocus
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                onBlur={() => saveEdit(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveEdit(item);
                  if (e.key === 'Escape') setEditingId(null);
                }}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: '1px solid #3a3a52',
                  borderRadius: 4,
                  color: '#e0e0e0',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  padding: '2px 6px',
                  outline: 'none',
                }}
              />
            ) : (
              <div
                onClick={() => { setEditingId(item.id); setEditingTitle(item.title); }}
                style={{
                  fontSize: 13,
                  color: item.status === 'done' ? '#666' : '#e0e0e0',
                  textDecoration: item.status === 'done' ? 'line-through' : 'none',
                  cursor: 'text',
                  lineHeight: 1.5,
                  wordBreak: 'break-word',
                }}
              >
                {item.title}
              </div>
            )}
            {item.source === 'ai' && (
              <div style={{ fontSize: 9, color: '#c8a96e', marginTop: 2, opacity: 0.7, letterSpacing: '0.05em' }}>
                proposed by Shift
              </div>
            )}
          </div>

          {notesByItem[item.id] > 0 && (
            <button
              onClick={() => onFilterByItem(item.id)}
              title="Show linked notes"
              style={{
                flexShrink: 0,
                background: 'rgba(155,143,196,0.1)',
                border: '1px solid rgba(155,143,196,0.3)',
                borderRadius: 10,
                color: '#9b8fc4',
                fontSize: 10,
                padding: '1px 8px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {notesByItem[item.id]} {notesByItem[item.id] === 1 ? 'note' : 'notes'}
            </button>
          )}

          <button
            onClick={() => deleteItem(item)}
            title="Delete"
            style={{
              flexShrink: 0,
              background: 'none',
              border: 'none',
              color: '#444',
              fontSize: 14,
              cursor: 'pointer',
              padding: '0 4px',
              opacity: 0.5,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#c46050'; e.currentTarget.style.opacity = 1; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#444'; e.currentTarget.style.opacity = 0.5; }}
          >
            ✕
          </button>
        </div>
      ))}

      {/* AI proposals are inserted at the END of the list — drawn as
          ghost rows by ProposedItemRow above. After Accept they convert
          to a normal row (proposed=false) and stay in the same place. */}

      {adding ? (
        <div style={{ marginTop: 8, padding: '8px 10px', display: 'flex', gap: 8 }}>
          <input
            autoFocus
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addItem();
              if (e.key === 'Escape') { setAdding(false); setNewTitle(''); }
            }}
            placeholder="What's the next step?"
            style={{
              flex: 1,
              background: '#13131a',
              border: '1px solid #3a3a52',
              borderRadius: 4,
              color: '#e0e0e0',
              fontFamily: 'inherit',
              fontSize: 13,
              padding: '6px 10px',
              outline: 'none',
            }}
          />
          <button
            onClick={addItem}
            style={{
              background: '#c8a96e',
              border: 'none',
              borderRadius: 4,
              color: '#07070a',
              fontSize: 12,
              padding: '6px 14px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontWeight: 600,
            }}
          >
            Add
          </button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          style={{
            marginTop: items.length > 0 ? 12 : 0,
            background: 'none',
            border: '1px dashed #2a2a2a',
            borderRadius: 6,
            color: '#666',
            fontSize: 12,
            padding: '8px 14px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            width: '100%',
            textAlign: 'left',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#c8a96e'; e.currentTarget.style.borderColor = '#c8a96e'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#666'; e.currentTarget.style.borderColor = '#2a2a2a'; }}
        >
          + Add item
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// NOTES PANEL
// ═══════════════════════════════════════════════════════════
function NotesPanel({ notes, allItems, filter, filterLabel, onClearFilter, onSetFilter, workspaceId, userId, onChanged, loading }) {
  const [adding, setAdding] = useState(false);
  const [draftBody, setDraftBody] = useState('');
  const [draftTag, setDraftTag] = useState(null);
  const [draftItemId, setDraftItemId] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [editTag, setEditTag] = useState(null);
  const [editItemId, setEditItemId] = useState(null);

  async function addNote() {
    const body = draftBody.trim();
    if (!body) return;
    await fetch('/api/project-excavate?resource=notes', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        workspace_id: workspaceId,
        user_id: userId,
        body,
        tag: draftTag,
        progress_item_id: draftItemId,
      }),
    });
    setDraftBody('');
    setDraftTag(null);
    setDraftItemId(null);
    setAdding(false);
    onChanged();
  }

  async function saveEdit(note) {
    const body = editBody.trim();
    if (!body) return;
    await fetch('/api/project-excavate?resource=notes', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: note.id, body, tag: editTag, progress_item_id: editItemId }),
    });
    setEditingId(null);
    onChanged();
  }

  async function deleteNote(note) {
    if (!window.confirm('Delete this note?')) return;
    await fetch(`/api/project-excavate?resource=notes&id=${note.id}`, { method: 'DELETE' });
    onChanged();
  }

  // ── Accept a proposed note — flips proposed=false ─────
  async function acceptNote(note) {
    await fetch('/api/project-excavate?resource=notes', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: note.id, proposed: false }),
    });
    onChanged();
  }

  async function dismissNote(note) {
    await fetch(`/api/project-excavate?resource=notes&id=${note.id}`, { method: 'DELETE' });
    onChanged();
  }

  function startEdit(note) {
    setEditingId(note.id);
    setEditBody(note.body);
    setEditTag(note.tag || null);
    setEditItemId(note.progress_item_id || null);
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
      {/* ── Add button ──────────────────────────────── */}
      {!adding && (
        <button
          onClick={() => setAdding(true)}
          style={{
            background: 'none',
            border: '1px dashed #2a2a2a',
            borderRadius: 6,
            color: '#666',
            fontSize: 12,
            padding: '8px 14px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            width: '100%',
            textAlign: 'left',
            marginBottom: 12,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#9b8fc4'; e.currentTarget.style.borderColor = '#9b8fc4'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#666'; e.currentTarget.style.borderColor = '#2a2a2a'; }}
        >
          + Note
        </button>
      )}

      {adding && (
        <NoteEditor
          body={draftBody}
          tag={draftTag}
          progressItemId={draftItemId}
          allItems={allItems}
          onChangeBody={setDraftBody}
          onChangeTag={setDraftTag}
          onChangeItem={setDraftItemId}
          onSubmit={addNote}
          onCancel={() => { setAdding(false); setDraftBody(''); setDraftTag(null); setDraftItemId(null); }}
          submitLabel="Save"
        />
      )}

      {/* ── Filter chips ───────────────────────────── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        <FilterChip
          active={!filter}
          onClick={onClearFilter}
          color="#888"
        >
          All
        </FilterChip>
        {TAG_OPTIONS.map(t => (
          <FilterChip
            key={t.id}
            active={filter === t.id}
            onClick={() => onSetFilter(t.id)}
            color={t.color}
          >
            {t.label}
          </FilterChip>
        ))}
        <FilterChip
          active={filter === 'unlinked'}
          onClick={() => onSetFilter('unlinked')}
          color="#666"
        >
          Unlinked
        </FilterChip>
      </div>

      {filter && allItems.find(i => i.id === filter) && (
        <div
          style={{
            marginBottom: 12,
            padding: '6px 10px',
            background: 'rgba(155,143,196,0.08)',
            border: '1px solid rgba(155,143,196,0.2)',
            borderRadius: 4,
            fontSize: 11,
            color: '#9b8fc4',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span>Linked to: <strong style={{ fontWeight: 600 }}>{filterLabel}</strong></span>
          <div style={{ flex: 1 }} />
          <button
            onClick={onClearFilter}
            style={{ background: 'none', border: 'none', color: '#9b8fc4', cursor: 'pointer', fontSize: 14, padding: 0 }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Notes list ─────────────────────────────── */}
      {loading && notes.length === 0 && (
        <div style={{ color: '#666', fontSize: 12, fontStyle: 'italic' }}>Loading…</div>
      )}

      {!loading && notes.length === 0 && (
        <div style={{ color: '#666', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>
          <div style={{ marginBottom: 8 }}>No notes {filter ? 'matching this filter.' : 'yet.'}</div>
          {!filter && (
            <div style={{ fontSize: 11, opacity: 0.6 }}>
              Capture findings, questions, decisions. Shift can propose notes from the chat too.
            </div>
          )}
        </div>
      )}

      {notes.map(note => {
        if (note.proposed) {
          return (
            <ProposedNoteRow
              key={note.id}
              note={note}
              onAccept={() => acceptNote(note)}
              onDismiss={() => dismissNote(note)}
            />
          );
        }
        const tag = note.tag ? TAG_BY_ID[note.tag] : null;
        const linkedItem = note.progress_item_id ? allItems.find(i => i.id === note.progress_item_id) : null;
        const isEditing = editingId === note.id;
        return (
          <div
            key={note.id}
            style={{
              padding: '10px 12px',
              borderRadius: 6,
              marginBottom: 8,
              background: '#111118',
              border: '1px solid #1e1e2a',
            }}
          >
            {isEditing ? (
              <NoteEditor
                body={editBody}
                tag={editTag}
                progressItemId={editItemId}
                allItems={allItems}
                onChangeBody={setEditBody}
                onChangeTag={setEditTag}
                onChangeItem={setEditItemId}
                onSubmit={() => saveEdit(note)}
                onCancel={() => setEditingId(null)}
                submitLabel="Save"
              />
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                  {tag && (
                    <span
                      style={{
                        flexShrink: 0,
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: tag.color,
                        background: `${tag.color}1a`,
                        border: `1px solid ${tag.color}55`,
                        padding: '1px 6px',
                        borderRadius: 3,
                        fontFamily: 'Georgia, serif',
                        marginTop: 2,
                      }}
                    >
                      {tag.label}
                    </span>
                  )}
                  <div
                    style={{
                      flex: 1,
                      fontSize: 13,
                      color: '#e0e0e0',
                      lineHeight: 1.55,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      cursor: 'text',
                    }}
                    onClick={() => startEdit(note)}
                  >
                    {note.body}
                  </div>
                  <button
                    onClick={() => deleteNote(note)}
                    title="Delete"
                    style={{
                      flexShrink: 0,
                      background: 'none',
                      border: 'none',
                      color: '#444',
                      fontSize: 14,
                      cursor: 'pointer',
                      padding: '0 4px',
                      opacity: 0.5,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#c46050'; e.currentTarget.style.opacity = 1; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#444'; e.currentTarget.style.opacity = 0.5; }}
                  >
                    ✕
                  </button>
                </div>

                {linkedItem && (
                  <button
                    onClick={() => onSetFilter(linkedItem.id)}
                    style={{
                      background: 'rgba(200,169,110,0.08)',
                      border: '1px solid rgba(200,169,110,0.25)',
                      borderRadius: 10,
                      color: '#c8a96e',
                      fontSize: 10,
                      padding: '2px 8px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={linkedItem.title}
                  >
                    → {linkedItem.title}
                  </button>
                )}

                {note.source === 'ai' && (
                  <div style={{ fontSize: 9, color: '#9b8fc4', marginTop: 4, opacity: 0.7, letterSpacing: '0.05em' }}>
                    proposed by Shift
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── NoteEditor (shared between add + edit) ──────────────────
function NoteEditor({ body, tag, progressItemId, allItems, onChangeBody, onChangeTag, onChangeItem, onSubmit, onCancel, submitLabel }) {
  return (
    <div
      style={{
        marginBottom: 12,
        padding: 10,
        background: '#0c0c11',
        border: '1px solid #3a3a52',
        borderRadius: 6,
      }}
    >
      <textarea
        autoFocus
        value={body}
        onChange={(e) => onChangeBody(e.target.value)}
        placeholder="Capture a thought, finding, question…"
        rows={3}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          color: '#e0e0e0',
          fontFamily: 'inherit',
          fontSize: 13,
          lineHeight: 1.5,
          padding: 0,
          outline: 'none',
          resize: 'vertical',
          minHeight: 50,
        }}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
        <span style={{ fontSize: 10, color: '#666', alignSelf: 'center', marginRight: 4 }}>Tag:</span>
        {TAG_OPTIONS.map(t => (
          <button
            key={t.id}
            onClick={() => onChangeTag(tag === t.id ? null : t.id)}
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontFamily: 'Georgia, serif',
              padding: '2px 7px',
              borderRadius: 3,
              cursor: 'pointer',
              background: tag === t.id ? `${t.color}25` : 'transparent',
              border: `1px solid ${tag === t.id ? t.color : '#2a2a2a'}`,
              color: tag === t.id ? t.color : '#888',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {allItems.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, color: '#666' }}>Link to:</span>
          <select
            value={progressItemId || ''}
            onChange={(e) => onChangeItem(e.target.value || null)}
            style={{
              flex: 1,
              background: '#13131a',
              border: '1px solid #3a3a52',
              borderRadius: 4,
              color: '#e0e0e0',
              fontFamily: 'inherit',
              fontSize: 11,
              padding: '3px 6px',
              outline: 'none',
            }}
          >
            <option value="">— none —</option>
            {allItems.map(item => (
              <option key={item.id} value={item.id}>
                {item.title.length > 60 ? item.title.slice(0, 60) + '…' : item.title}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10 }}>
        <button
          onClick={onCancel}
          style={{
            background: 'none',
            border: '1px solid #2a2a2a',
            borderRadius: 4,
            color: '#888',
            fontSize: 11,
            padding: '4px 12px',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Cancel
        </button>
        <button
          onClick={onSubmit}
          disabled={!body.trim()}
          style={{
            background: body.trim() ? '#9b8fc4' : '#2a2a2a',
            border: 'none',
            borderRadius: 4,
            color: body.trim() ? '#07070a' : '#555',
            fontSize: 11,
            padding: '4px 14px',
            cursor: body.trim() ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit',
            fontWeight: 600,
          }}
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PROPOSED ROWS — ghost rows for items + notes proposed by Shift.
// Distinct visual treatment so they read as suggestions, not commits.
// User accepts (✓ Add) or dismisses (✕). Both are one-click — no
// confirmation since dismiss is recoverable (Shift will likely
// re-propose if it matters).
// ═══════════════════════════════════════════════════════════

function ProposedItemRow({ item, onAccept, onDismiss }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 6,
        marginBottom: 4,
        background: 'rgba(200,169,110,0.04)',
        border: '1px dashed rgba(200,169,110,0.4)',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#c8a96e',
          fontFamily: 'Georgia, serif',
        }}
      >
        Shift
      </span>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          color: '#c8a96e',
          opacity: 0.95,
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
      >
        {item.title}
      </div>
      <button
        onClick={onAccept}
        title="Add to Progress"
        style={{
          flexShrink: 0,
          background: 'rgba(122,158,130,0.15)',
          border: '1px solid #7a9e82',
          borderRadius: 4,
          color: '#7a9e82',
          fontSize: 11,
          padding: '3px 10px',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontWeight: 600,
        }}
      >
        ✓ Add
      </button>
      <button
        onClick={onDismiss}
        title="Dismiss"
        style={{
          flexShrink: 0,
          background: 'none',
          border: '1px solid #2a2a2a',
          borderRadius: 4,
          color: '#666',
          fontSize: 11,
          padding: '3px 10px',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        ✕
      </button>
    </div>
  );
}

function ProposedNoteRow({ note, onAccept, onDismiss }) {
  const tag = note.tag ? TAG_BY_ID[note.tag] : null;
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 6,
        marginBottom: 8,
        background: 'rgba(155,143,196,0.04)',
        border: '1px dashed rgba(155,143,196,0.4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#9b8fc4',
            fontFamily: 'Georgia, serif',
          }}
        >
          Shift suggests
        </span>
        {tag && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: tag.color,
              background: `${tag.color}1a`,
              border: `1px solid ${tag.color}55`,
              padding: '1px 6px',
              borderRadius: 3,
              fontFamily: 'Georgia, serif',
            }}
          >
            {tag.label}
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 13,
          color: '#c0b8d8',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          marginBottom: 8,
        }}
      >
        {note.body}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <button
          onClick={onDismiss}
          title="Dismiss"
          style={{
            background: 'none',
            border: '1px solid #2a2a2a',
            borderRadius: 4,
            color: '#666',
            fontSize: 11,
            padding: '3px 10px',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          ✕ Dismiss
        </button>
        <button
          onClick={onAccept}
          title="Add to Notes"
          style={{
            background: 'rgba(122,158,130,0.15)',
            border: '1px solid #7a9e82',
            borderRadius: 4,
            color: '#7a9e82',
            fontSize: 11,
            padding: '3px 10px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontWeight: 600,
          }}
        >
          ✓ Add
        </button>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, color, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.05em',
        padding: '3px 9px',
        borderRadius: 10,
        cursor: 'pointer',
        background: active ? `${color}25` : 'transparent',
        border: `1px solid ${active ? color : '#2a2a2a'}`,
        color: active ? color : '#888',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}
